/**
 * ehr#741 (Chase 10-31, points 4 to 8; PROGRESS NOTE V132 part 2): the state of a field-change
 * card and the order the review cards show in. Plain functions so they can be tested without the
 * editor.
 *
 * - A card is waiting (white), kept with its check (green) or not kept with its red X (red). The
 *   decision lives on the card's thread (body.reviewThreads): `resolved` or `denied`, never both.
 * - A card the person closed with the red X on their own change goes, with its replies and tick,
 *   saved or not: its thread is left closed and empty (the server keeps it that way), so a later
 *   change to the same field starts a new card instead of bringing the old back.
 * - Cards sit in the order of the fields they are about, top to bottom and left to right on the
 *   note, not in the order they were made.
 */
import type { NoteFieldChange, ReviewThread } from '@ehr/shared';
import { cardThreadId } from '@ehr/shared';
import { withoutClosed } from './reviewCards.js';

export type CardState = 'pending' | 'kept' | 'denied';

/** A field-change card's state from its thread. */
export function cardState(
  t: Pick<ReviewThread, 'resolved' | 'denied' | 'closed'> | undefined,
): CardState {
  if (t?.closed) return 'pending';
  if (t?.resolved) return 'kept';
  if (t?.denied) return 'denied';
  return 'pending';
}

/**
 * The card's decision set: kept (the check), denied (the X) or neither (waiting). A thread is made
 * for a card that has none yet.
 */
export function withDecision(
  threads: readonly ReviewThread[],
  t: { id: string; section: string },
  me: { by: string; round: number },
  state: CardState,
): ReviewThread[] {
  threads = withoutClosed(threads, t.id);
  const set = (x: ReviewThread): ReviewThread => {
    const { resolved: _r, denied: _d, ...rest } = x;
    void _r;
    void _d;
    if (state === 'kept') return { ...rest, resolved: true };
    if (state === 'denied') return { ...rest, denied: true };
    return rest;
  };
  if (threads.some((x) => x.id === t.id)) return threads.map((x) => (x.id === t.id ? set(x) : x));
  if (state === 'pending') return [...threads];
  return [
    ...threads,
    set({ id: t.id, section: t.section, by: me.by, at: '', round: me.round, replies: [] }),
  ];
}

/**
 * The person's own cards that a red X closes: their cards on the note before the field was put
 * back that are gone after it (a time takes its length with it), saved on the note or not. A card
 * of the other side is never closed here; its X marks it not kept instead.
 */
export function closedCards(
  before: readonly Pick<NoteFieldChange, 'key'>[],
  after: readonly Pick<NoteFieldChange, 'key'>[],
  side: 's' | 'a',
): string[] {
  const still = new Set(after.map((c) => c.key));
  return before.map((c) => c.key).filter((k) => k.startsWith(`${side}:`) && !still.has(k));
}

/**
 * Threads with the given cards' threads ("fc:<key>") closed: no replies, no tick, marked closed so
 * the server empties its copy too and the card never comes back. A card with no thread needs none.
 */
export function withClosedCards(
  threads: readonly ReviewThread[],
  keys: readonly string[],
  me: { by: string; round: number },
): ReviewThread[] {
  if (!keys.length) return [...threads];
  const ids = new Set(keys.map((k) => `fc:${k}`));
  return threads.map((t) =>
    ids.has(t.id)
      ? {
          id: t.id,
          section: t.section,
          by: me.by,
          at: '',
          round: me.round,
          replies: [],
          closed: true,
        }
      : t,
  );
}

/**
 * Field-change cards in the order of their fields on the note. `fields` is the note's tracked
 * fields in the order they show (a field snapshot's keys); a card for a field not on the note goes
 * last. Two cards on one field (the reviewer's and the author's) keep their order.
 */
export function orderFieldCards<T extends Pick<NoteFieldChange, 'key'>>(
  cards: readonly T[],
  fields: readonly string[],
): T[] {
  const rank = (c: T) => {
    const i = fields.indexOf(c.key.slice(2));
    return i < 0 ? fields.length : i;
  };
  return cards
    .map((c, i) => ({ c, i, r: rank(c) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.c);
}

/**
 * Comments on a text box in the order of the words they are on: a comment on the whole box first,
 * then by where its words start. Comments at one place keep their order.
 */
export function orderByPlace<T extends { quote?: string; pos?: number }>(items: readonly T[]): T[] {
  const at = (x: T) => (x.quote ? (x.pos ?? Number.MAX_SAFE_INTEGER) : -1);
  return items
    .map((x, i) => ({ x, i }))
    .sort((a, b) => at(a.x) - at(b.x) || a.i - b.i)
    .map((y) => y.x);
}

// ── ehr#890 (Chase 11-02, points 3 and 4): Problems, goals, and objectives addressed and
// Interventions used show what was added on one card and what was removed on another, each with
// its own check, X and replies. The note keeps one change per field (body.fieldChanges, as
// before); the split is how the margin shows it. Each half's conversation is "fc:<key>#add" or
// "fc:<key>#rem"; a note saved with the one combined card ("fc:<key>") reads that card's decision
// on both halves and its replies on the first.

/** The fields whose card is split into an Added card and a Removed card. */
export const SPLIT_FIELDS: readonly string[] = ['focus', 'interventions'];

/** One card in the margin: a whole field change, or the Added or Removed half of a list's. */
export interface CardPart {
  /** The card's thread id: "fc:<key>", or "fc:<key>#add" / "fc:<key>#rem" for a half. */
  id: string;
  card: NoteFieldChange;
  half?: 'add' | 'rem';
  /** The items the half names (its labels, as the card shows them). */
  items?: string[];
  text: string;
}

const NONE_WORD = 'None';

/** A list item's words as its card names it: a chip's label, an intervention's name. */
const itemWord = (x: unknown) =>
  x && typeof x === 'object' ? String((x as { label?: unknown }).label ?? '') : String(x);

/** The item words of list fields' values (JSON); a value that isn't a list adds none. */
export function rawLabels(...raws: readonly (string | undefined)[]): string[] {
  const out: string[] = [];
  for (const r of raws) {
    if (r === undefined) continue;
    try {
      const v = JSON.parse(r) as unknown;
      if (Array.isArray(v)) out.push(...v.map(itemWord));
    } catch {
      // not JSON: no words from it
    }
  }
  return out;
}

/**
 * A list card's words split back into its items. The card joins them with ", ", and a goal or
 * objective can have commas of its own ("Reduce frequency, intensity, and duration of panic"), so
 * pieces that make up a known item (`known`, the labels the lists hold) are joined back, the
 * longest first. A piece no known item explains stays an item of its own.
 */
export function splitItems(v: string, known: readonly string[] = []): string[] {
  if (v === NONE_WORD || !v) return [];
  const bits = v.split(', ');
  const set = new Set(known);
  const out: string[] = [];
  for (let i = 0; i < bits.length;) {
    let j = bits.length;
    while (j > i + 1 && !set.has(bits.slice(i, j).join(', '))) j--;
    out.push(bits.slice(i, j).join(', '));
    i = j;
  }
  return out;
}

/**
 * The cards a field change shows as: one, or for a split list an Added card and a Removed card.
 * `known` names the items the field's lists hold (the note now, the note as it opened); the card's
 * own value before the change is added, so an item with a comma in it stays one item.
 */
export function cardParts(
  c: NoteFieldChange,
  text: (c: NoteFieldChange) => string,
  known: readonly string[] = [],
): CardPart[] {
  if (!SPLIT_FIELDS.includes(c.key.slice(2)))
    return [{ id: cardThreadId(c.key), card: c, text: text(c) }];
  const words = [...known, ...rawLabels(c.fromRaw)];
  const a = splitItems(c.from, words);
  const b = splitItems(c.to, words);
  const added = b.filter((x) => !a.includes(x));
  const removed = a.filter((x) => !b.includes(x));
  const out: CardPart[] = [];
  if (added.length)
    out.push({
      id: `${cardThreadId(c.key)}#add`,
      card: c,
      half: 'add',
      items: added,
      text: `Added: ${added.join(', ')}`,
    });
  if (removed.length)
    out.push({
      id: `${cardThreadId(c.key)}#rem`,
      card: c,
      half: 'rem',
      items: removed,
      text: `Removed: ${removed.join(', ')}`,
    });
  return out.length ? out : [{ id: cardThreadId(c.key), card: c, text: text(c) }];
}

/** The combined card's thread id a half's thread id came from ("fc:a:focus#add" → "fc:a:focus"). */
export const wholeThreadId = (id: string) => id.replace(/#(add|rem)$/, '');

/**
 * A card's thread as the margin reads it. A half with no thread of its own yet, on a note saved
 * with the combined card, takes that card's decision; the first half shows its replies too.
 */
export function partThread(
  threads: readonly ReviewThread[],
  part: Pick<CardPart, 'id' | 'half'>,
  first: boolean,
): ReviewThread | undefined {
  const open = (id: string) => threads.find((t) => t.id === id && !t.closed);
  const own = open(part.id);
  if (!part.half) return own;
  const old = open(wholeThreadId(part.id));
  if (!old) return own;
  const replies = [...(first ? old.replies : []), ...(own?.replies ?? [])];
  if (own) return { ...own, replies };
  const { resolved, denied } = old;
  return {
    id: part.id,
    section: old.section,
    by: old.by,
    at: old.at,
    round: old.round,
    replies,
    ...(resolved ? { resolved } : {}),
    ...(denied ? { denied } : {}),
  };
}

/**
 * Threads ready for a change on a half: a half with no thread yet, on a note saved with the
 * combined card, starts from that card's decision, so taking a tick off a half works.
 */
export function withPartSeeded(
  threads: readonly ReviewThread[],
  part: Pick<CardPart, 'id' | 'half'>,
  me: { by: string; round: number },
): ReviewThread[] {
  if (!part.half || threads.some((t) => t.id === part.id && !t.closed)) return [...threads];
  const old = threads.find((t) => t.id === wholeThreadId(part.id) && !t.closed);
  if (!old || (!old.resolved && !old.denied)) return [...threads];
  return [
    ...threads.filter((t) => t.id !== part.id),
    {
      id: part.id,
      section: old.section,
      by: me.by,
      at: '',
      round: me.round,
      replies: [],
      ...(old.resolved ? { resolved: true } : { denied: true }),
    },
  ];
}

/** Threads with the given thread ids closed (no replies, no tick), as withClosedCards does. */
export function withClosedThreads(
  threads: readonly ReviewThread[],
  ids: readonly string[],
  me: { by: string; round: number },
): ReviewThread[] {
  return withClosedCards(
    threads,
    ids.map((id) => id.slice(3)),
    me,
  );
}

/**
 * A list field with a half's items taken out ("drop") or put in ("put", copied from `source`,
 * the list as it was where they were): the red X on an Added card drops what it added, on a
 * Removed card puts back what it removed; the check on a red card does the opposite. Undefined
 * when an item to put in can't be found in `source` (a card saved before it kept the list).
 */
export function withHalfItems(
  now: unknown,
  items: readonly string[],
  how: 'drop' | 'put',
  source?: unknown,
  /** The list holds chips (Problems, goals, and objectives addressed), not plain words. */
  chips = false,
): unknown[] | undefined {
  const list = Array.isArray(now) ? now : [];
  if (how === 'drop') return list.filter((x) => !items.includes(itemWord(x)));
  const have = new Set(list.map(itemWord));
  const from = Array.isArray(source) ? source : [];
  const add: unknown[] = [];
  for (const w of items) {
    if (have.has(w)) continue;
    const hit = from.find((x) => itemWord(x) === w);
    // an intervention is its own words; a chip needs the list it came from
    if (hit !== undefined) add.push(hit);
    else if (chips) return undefined;
    else add.push(w);
  }
  return [...list, ...add];
}

/**
 * A list field's value (JSON) with a half's items dropped or put back (withHalfItems), from the
 * field's value now and the list the items came from, both as JSON. Undefined when it can't.
 */
export function halfRaw(
  field: string,
  nowRaw: string | undefined,
  items: readonly string[],
  how: 'drop' | 'put',
  sourceRaw: string | undefined,
): string | undefined {
  const read = (r: string | undefined): unknown => {
    try {
      return r === undefined ? undefined : (JSON.parse(r) as unknown);
    } catch {
      return undefined;
    }
  };
  const next = withHalfItems(read(nowRaw), items, how, read(sourceRaw), field === 'focus');
  return next === undefined ? undefined : JSON.stringify(next);
}

/**
 * ehr#890 (Chase 11-04): what the red X does to a field-change card. The person who made the card
 * ("s:" the reviewer's, "a:" the author's): the change is put back and the card goes, with its
 * replies ("close"). The other person: the change is put back and the card turns red ("deny").
 */
export function xAction(cardKey: string, side: 's' | 'a' | null): 'close' | 'deny' {
  return side !== null && cardKey.startsWith(`${side}:`) ? 'close' : 'deny';
}
